import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import useXHR from '../../hooks/useXHR';
import { API } from '../../utils/api';
const TONE_META = {
  hurricane: { icon: '🌪️', color: '#ef4444', label: 'Hurricane Force' },
  snarky: { icon: '😏', color: '#047857', label: 'Dry & Snarky' },
  cinematic: { icon: '🎬', color: '#047857', label: 'Movie Trailer' },
  social: { icon: '📱', color: '#3b82f6', label: 'Social Media' },
  testimonial: { icon: '⭐', color: '#f59e0b', label: 'Fake Reviews' },
  comparison: { icon: '🔀', color: '#10b981', label: 'Absurd Comparisons' },
  holiday: { icon: '🎄', color: '#047857', label: 'Holiday Special' },
};

const AdCopyGenerator = ({ showToast }) => {
  const { token } = useAuth();
  const { get, post, del: xhrDel, put } = useXHR(token);
  const [selectedTone, setSelectedTone] = useState('hurricane');
  const [customPrompt, setCustomPrompt] = useState('');
  const [count, setCount] = useState(3);
  const [copies, setCopies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [copiedIdx, setCopiedIdx] = useState(null);

  const generateCopy = async () => {
    if (!token) return;
    setLoading(true);
    setCopies([]);
    try {
      const data = await post('/ad-copy/generate', {
        body: { tone: selectedTone, custom_prompt: customPrompt, count }
      });
      if (data.success) {
        setCopies(data.copies);
        if (showToast) showToast('Ad copy generated!', 'success');
      } else {
        if (showToast) showToast('Generation failed', 'error');
      }
    } catch (e) {
      console.error('Generation failed:', e);
      if (showToast) showToast('Generation failed', 'error');
    }
    setLoading(false);
  };

  const fetchHistory = async () => {
    if (!token) return;
    try {
      const data = await get('/ad-copy/history');
      if (data.history) setHistory(data.history);
    } catch (e) {
      console.log('History fetch failed');
    }
  };

  useEffect(() => {
    if (showHistory) fetchHistory();
  }, [showHistory, token]);

  const copyToClipboard = async (text, idx) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIdx(idx);
      setTimeout(() => setCopiedIdx(null), 2000);
    } catch (e) {
      console.error('Copy failed');
    }
  };

  return (
    <div style={{
      background: 'linear-gradient(135deg, rgba(239,68,68,0.08), rgba(251,191,36,0.08), rgba(252, 211, 77,0.06))',
      borderRadius: 20,
      padding: 28,
      marginBottom: 24,
      border: '1px solid rgba(239,68,68,0.25)',
    }} data-testid="ad-copy-generator">
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h3 style={{ 
            color: '#0F172A', margin: 0, fontSize: '1.3rem', fontWeight: 700,
            display: 'flex', alignItems: 'center', gap: 10
          }}>
            <span style={{ fontSize: '1.6rem' }}>🌪️</span>
            AI Ad Copy Generator
          </h3>
          <p style={{ color: '#a1a1aa', fontSize: '0.85rem', margin: '6px 0 0 0' }}>
            Generate hurricane-force ad copy for "Letters to Evelyn"
          </p>
        </div>
        <button
          onClick={() => setShowHistory(!showHistory)}
          data-testid="ad-copy-history-toggle"
          style={{
            background: showHistory ? 'rgba(252, 211, 77,0.25)' : 'rgba(203, 213, 225, 0.8)',
            border: '1px solid rgba(252, 211, 77,0.3)',
            borderRadius: 10,
            padding: '8px 16px',
            color: '#047857',
            cursor: 'pointer',
            fontSize: '0.85rem',
            fontWeight: 600
          }}
        >
          {showHistory ? 'Hide History' : 'View History'}
        </button>
      </div>

      {/* Tone selector */}
      <div style={{ marginBottom: 20 }}>
        <label style={{ color: '#d4d4d8', fontSize: '0.85rem', fontWeight: 600, marginBottom: 10, display: 'block' }}>
          Choose Your Weapon (Tone):
        </label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {Object.entries(TONE_META).map(([id, meta]) => (
            <button
              key={id}
              onClick={() => setSelectedTone(id)}
              data-testid={`tone-${id}`}
              style={{
                background: selectedTone === id 
                  ? `linear-gradient(135deg, ${meta.color}30, ${meta.color}18)` 
                  : 'rgba(0,0,0,0.25)',
                border: selectedTone === id 
                  ? `2px solid ${meta.color}80` 
                  : '1px solid rgba(255,255,255,0.1)',
                borderRadius: 12,
                padding: '10px 16px',
                color: selectedTone === id ? '#fff' : '#a1a1aa',
                cursor: 'pointer',
                fontSize: '0.85rem',
                fontWeight: selectedTone === id ? 700 : 400,
                transition: 'all 0.2s ease'
              }}
            >
              {meta.icon} {meta.label}
            </button>
          ))}
        </div>
      </div>

      {/* Custom prompt */}
      <div style={{ marginBottom: 20 }}>
        <label style={{ color: '#d4d4d8', fontSize: '0.85rem', fontWeight: 600, marginBottom: 8, display: 'block' }}>
          Extra Direction (optional):
        </label>
        <textarea
          value={customPrompt}
          onChange={(e) => setCustomPrompt(e.target.value)}
          placeholder="e.g., Focus on the oven incident, or Make it about Easter eggs..."
          data-testid="ad-copy-custom-prompt"
          style={{
            width: '100%',
            background: 'rgba(0,0,0,0.3)',
            border: '1px solid rgba(255,255,255,0.15)',
            borderRadius: 12,
            padding: 14,
            color: '#0F172A',
            fontSize: '0.9rem',
            resize: 'vertical',
            minHeight: 60,
            maxHeight: 120,
            outline: 'none',
            boxSizing: 'border-box'
          }}
        />
      </div>

      {/* Count & Generate */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <label style={{ color: '#a1a1aa', fontSize: '0.85rem' }}>Variations:</label>
          <select
            value={count}
            onChange={(e) => setCount(Number(e.target.value))}
            data-testid="ad-copy-count"
            style={{
              background: 'rgba(0,0,0,0.3)',
              border: '1px solid rgba(255,255,255,0.15)',
              borderRadius: 8,
              padding: '8px 12px',
              color: '#0F172A',
              fontSize: '0.85rem',
              cursor: 'pointer'
            }}
          >
            {[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
        
        <button
          onClick={generateCopy}
          disabled={loading || !token}
          data-testid="generate-ad-copy-btn"
          style={{
            background: loading 
              ? 'rgba(239,68,68,0.2)' 
              : 'linear-gradient(135deg, #ef4444, #f59e0b)',
            border: 'none',
            borderRadius: 12,
            padding: '12px 28px',
            color: '#0F172A',
            fontSize: '0.95rem',
            fontWeight: 700,
            cursor: loading ? 'wait' : 'pointer',
            opacity: loading ? 0.7 : 1,
            transition: 'all 0.2s ease',
            boxShadow: loading ? 'none' : '0 4px 16px rgba(239,68,68,0.3)'
          }}
        >
          {loading ? 'Generating...' : '🌪️ Generate Ad Copy'}
        </button>
      </div>

      {/* Results */}
      {copies.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <h4 style={{ color: '#047857', margin: '0 0 14px 0', fontSize: '1rem' }}>
            Generated Copy:
          </h4>
          <div style={{ display: 'grid', gap: 12 }}>
            {copies.map((copy, idx) => (
              <div key={idx} style={{
                background: 'rgba(0,0,0,0.35)',
                borderRadius: 14,
                padding: 18,
                border: '1px solid rgba(251,191,36,0.2)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: 14
              }}>
                <p style={{ color: '#334155', margin: 0, fontSize: '0.95rem', lineHeight: 1.6, flex: 1 }} data-testid={`ad-copy-result-${idx}`}>
                  {copy}
                </p>
                <button
                  onClick={() => copyToClipboard(copy, idx)}
                  data-testid={`copy-ad-${idx}`}
                  style={{
                    background: copiedIdx === idx ? 'rgba(16,185,129,0.3)' : 'rgba(252, 211, 77,0.2)',
                    border: 'none',
                    borderRadius: 8,
                    padding: '8px 14px',
                    color: copiedIdx === idx ? '#10b981' : '#047857',
                    cursor: 'pointer',
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    flexShrink: 0
                  }}
                >
                  {copiedIdx === idx ? 'Copied!' : 'Copy'}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* History */}
      {showHistory && (
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: 20 }}>
          <h4 style={{ color: '#047857', margin: '0 0 14px 0' }}>Generation History</h4>
          {history.length === 0 ? (
            <p style={{ color: '#71717a', fontSize: '0.85rem' }}>No history yet. Generate some ad copy!</p>
          ) : (
            <div style={{ display: 'grid', gap: 10, maxHeight: 300, overflowY: 'auto' }}>
              {history.map((item, idx) => (
                <div key={idx} style={{
                  background: 'rgba(0,0,0,0.2)',
                  borderRadius: 10,
                  padding: 14,
                  border: '1px solid rgba(252, 211, 77,0.15)'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span style={{ color: '#047857', fontSize: '0.8rem', fontWeight: 600 }}>
                      {TONE_META[item.tone]?.icon} {TONE_META[item.tone]?.label || item.tone}
                    </span>
                    <span style={{ color: '#71717a', fontSize: '0.75rem' }}>
                      {item.created_at ? new Date(item.created_at).toLocaleDateString() : ''}
                    </span>
                  </div>
                  {item.generated_copies?.map((copy, i) => (
                    <p key={i} style={{ color: '#d1d5db', fontSize: '0.85rem', margin: '4px 0', lineHeight: 1.5 }}>
                      {copy}
                    </p>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdCopyGenerator;
